import { motion } from "framer-motion";
import { MANIFESTO, IMAGES, SPORTS } from "@/data";

const fadeUp = {
  hidden: { opacity: 0, y: 40 },
  show: (i = 0) => ({
    opacity: 1,
    y: 0,
    transition: { duration: 0.6, delay: i * 0.1, ease: [0.22, 1, 0.36, 1] },
  }),
};

export const About = () => {
  return (
    <section
      id="about"
      className="relative bg-slate-50 py-24 md:py-36"
      data-testid="about-section"
    >
      <div className="mx-auto max-w-7xl px-5">
        <motion.div
          initial="hidden"
          whileInView="show"
          viewport={{ once: true, margin: "-100px" }}
          variants={fadeUp}
          className="max-w-3xl"
        >
          <span className="inline-block font-display font-bold text-sky-500 uppercase tracking-widest text-sm mb-4">
            About Us
          </span>
          <h2 className="font-display font-black tracking-tight text-slate-900 text-4xl sm:text-5xl lg:text-6xl leading-[0.95]">
            A friendly place to <br />
            <span className="text-sky-500">spend the holidays.</span>
          </h2>
          <p className="mt-6 text-lg md:text-xl text-slate-600 font-medium">
            Run by our team of coaches at The Downs Preparatory School, Action
            Packed Adventures offers a warm, well-organised camp for children
            from Years 1 to 8 — with plenty of sport, games and space to
            play.
          </p>

          <div className="mt-8">
            <p className="font-display font-bold text-slate-500 uppercase tracking-widest text-xs mb-3">
              Sports &amp; Activities
            </p>
            <div className="flex flex-wrap gap-2.5" data-testid="sports-list">
              {SPORTS.map((sport) => (
                <span
                  key={sport}
                  data-testid={`sport-${sport.toLowerCase()}`}
                  className="rounded-full bg-white border-2 border-sky-100 px-4 py-2 font-bold text-slate-700 shadow-sm"
                >
                  {sport === "Swimming" ? "Swimming*" : sport}
                </span>
              ))}
            </div>
            <p className="mt-4 text-slate-600 font-medium">
              *No swimming at the Christmas camp — swimming takes place at our
              Easter and summer camps.
            </p>
          </div>
        </motion.div>

        <div className="mt-16 grid gap-6 lg:grid-cols-12">
          {/* Chapter 01 */}
          <motion.div
            custom={0}
            initial="hidden"
            whileInView="show"
            viewport={{ once: true }}
            variants={fadeUp}
            className="lg:col-span-7 rounded-[2rem] bg-white border-2 border-slate-900/5 p-8 md:p-10 shadow-[0_20px_50px_rgba(15,23,42,0.06)]"
          >
            <span className="font-display font-black text-7xl md:text-8xl text-sky-200 leading-none">
              {MANIFESTO[0].num}
            </span>
            <h3 className="mt-4 font-display font-extrabold text-2xl md:text-3xl text-slate-900">
              {MANIFESTO[0].title}
            </h3>
            <p className="mt-3 text-lg text-slate-600 font-medium max-w-xl">
              {MANIFESTO[0].body}
            </p>
          </motion.div>

          {/* Photo after 01 */}
          <motion.div
            custom={1}
            initial="hidden"
            whileInView="show"
            viewport={{ once: true }}
            variants={fadeUp}
            className="lg:col-span-5 overflow-hidden rounded-[2rem] border-2 border-white shadow-lg min-h-[18rem]"
          >
            <img
              src={IMAGES.coachesLogo}
              alt="Campers proudly showing off their colourful artwork"
              className="h-full w-full object-cover transition-transform duration-700 hover:scale-105"
            />
          </motion.div>

          {/* Chapter 02 */}
          <motion.div
            custom={0}
            initial="hidden"
            whileInView="show"
            viewport={{ once: true }}
            variants={fadeUp}
            className="lg:col-span-7 rounded-[2rem] bg-slate-900 p-8 md:p-10 shadow-xl text-white"
          >
            <span className="font-display font-black text-7xl md:text-8xl text-sky-500/40 leading-none">
              {MANIFESTO[1].num}
            </span>
            <h3 className="mt-4 font-display font-extrabold text-2xl md:text-3xl">
              {MANIFESTO[1].title}
            </h3>
            <p className="mt-3 text-lg text-slate-300 font-medium max-w-xl">
              {MANIFESTO[1].body}
            </p>
          </motion.div>

          {/* Photo after 02 */}
          <motion.div
            custom={1}
            initial="hidden"
            whileInView="show"
            viewport={{ once: true }}
            variants={fadeUp}
            className="lg:col-span-5 overflow-hidden rounded-[2rem] border-2 border-white shadow-lg min-h-[18rem]"
          >
            <img
              src={IMAGES.coachChild}
              alt="Two campers enjoying the woodland play area"
              className="h-full w-full object-cover object-center transition-transform duration-700 hover:scale-105"
            />
          </motion.div>

          {/* Chapter 03 */}
          <motion.div
            custom={0}
            initial="hidden"
            whileInView="show"
            viewport={{ once: true }}
            variants={fadeUp}
            className="lg:col-span-7 rounded-[2rem] bg-sky-400 p-8 md:p-10 shadow-xl overflow-hidden relative"
          >
            <div className="grain absolute inset-0 opacity-20" />
            <div className="relative z-10">
              <span className="font-display font-black text-7xl md:text-8xl text-white/40 leading-none">
                {MANIFESTO[2].num}
              </span>
              <h3 className="mt-4 font-display font-extrabold text-2xl md:text-3xl text-white">
                {MANIFESTO[2].title}
              </h3>
              <p className="mt-3 text-lg text-white/90 font-semibold max-w-xl">
                {MANIFESTO[2].body}
              </p>
            </div>
          </motion.div>

          {/* Photo after 03 */}
          <motion.div
            custom={1}
            initial="hidden"
            whileInView="show"
            viewport={{ once: true }}
            variants={fadeUp}
            className="lg:col-span-5 overflow-hidden rounded-[2rem] border-2 border-white shadow-lg min-h-[18rem]"
          >
            <img
              src={IMAGES.gallerySupplement}
              alt="Heading out to the playing field with a coach on a camp day"
              className="h-full w-full object-cover object-center transition-transform duration-700 hover:scale-105"
            />
          </motion.div>
        </div>
      </div>
    </section>
  );
};

export default About;
